// src/components/AIChatSide.js
import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Avatar,
  Typography,
  useTheme,
  IconButton,
  Paper,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
  Menu,
  MenuItem,
} from '@mui/material';
import { motion } from 'framer-motion';
import SendIcon from '@mui/icons-material/Send';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import aiIcon from '../assets/images/ai_icon.png';
import faviconIcon from '../assets/images/favicon.png';
import { translateFieldNames } from '../utils/fieldTranslator';
import { bg, border } from '../utils/darkModeAdapter';
import {
  designTokens,
  chatHeader,
  chatBackground,
  chatBubbleBot,
  chatBubbleUser,
  chatInputField,
  chatSendButton,
} from '../theme/designTokens';

/** 
 * 判定: 文字列が「パースできる JSON かつ オブジェクト/配列のみ」なら true
 * → GPTが strict JSON を返した場合、チャット表示をスキップするために使用
 */
function isPureJson(str) {
  try {
    const data = JSON.parse(str);
    if (typeof data === 'object' && data !== null) {
      return true;
    }
  } catch (e) {}
  return false;
}

/** 特殊なメッセージ判定 (例: "###採用計画" を含むかどうか) */
function isSpecialMessage(text) {
  return text.includes('###') && text.includes('採用計画');
}

/** ローディング時のアニメーション: "..." 吹き出し */
function TypingBubble() {
  const theme = useTheme();
  return (
    <Box sx={{ display: 'flex', mb: 1 }}>
      <Avatar
        alt="Bot"
        src={faviconIcon}
        sx={{
          width: 32,
          height: 32,
          mx: 1,
        }}
      />
      <Paper
        elevation={0}
        sx={{
          maxWidth: '70%',
          backgroundColor: bg(theme).hover,
          color: '#374151',
          borderRadius: '8px',
          p: 1.5,
          border: `1px solid ${border(theme).subtle}`,
        }}
      >
        <Typography variant="body2">...</Typography>
      </Paper>
    </Box>
  );
}

/** 一つのメッセージ吹き出し */
function ChatBubble({ message, featureKey }) {
  const theme = useTheme();
  const isBot = message.sender === 'bot';
  const [copied, setCopied] = React.useState(false);

  // GPTのレスポンスが純粋なJSONなら表示しない
  if (isPureJson(message.text)) {
    return null;
  }

  // botメッセージの場合はフィールド名を翻訳
  const displayText = isBot ? translateFieldNames(message.text) : message.text;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(displayText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('コピーに失敗しました:', err);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: isBot ? 'flex-start' : 'flex-end',
        mb: 1.5,
      }}
    >
      <Avatar
        alt={isBot ? 'Bot' : 'User'}
        src={isBot ? faviconIcon : undefined}
        sx={{
          width: 32,
          height: 32,
          alignSelf: 'flex-end',
          order: isBot ? 1 : 2,
          mx: 1,
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        }}
      />
      <Box sx={{ order: isBot ? 2 : 1, maxWidth: '80%' }}>
        <Paper
          elevation={0}
          sx={{
            ...(isBot ? chatBubbleBot(theme) : chatBubbleUser(featureKey)),
          }}
        >
          {isSpecialMessage(displayText) ? (
            <Box
              sx={{
                p: 2,
                backgroundColor: bg(theme).card,
                border: `1px solid ${theme.palette.primary.light}`,
                borderRadius: 2,
              }}
            >
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {displayText}
              </ReactMarkdown>
            </Box>
          ) : (
            <Box sx={{ '& p': { m: 0 } }}>
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {displayText}
              </ReactMarkdown>
            </Box>
          )}
        </Paper>
        {isBot && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.25 }}>
            <IconButton
              size="small"
              onClick={handleCopy}
              sx={{
                opacity: 0.4,
                '&:hover': { opacity: 1 },
                transition: 'opacity 0.2s',
                p: 0.25,
              }}
            >
              {copied ? (
                <CheckIcon sx={{ fontSize: 14, color: 'success.main' }} />
              ) : (
                <ContentCopyIcon sx={{ fontSize: 14 }} />
              )}
            </IconButton>
          </Box>
        )}
      </Box>
    </Box>
  );
}

/**
 * AIChatSide:
 * サイドバー固定のAIチャットコンポーネント。
 * 「mode」（"chat" or "plan"）で用途を切り替え、1つの「送信」ボタンから
 * メニューで「AIに相談」または「採用計画を修正」を選択する実装例です。
 * また、ウィンドウリサイズ時に画面右端に常に固定されるように位置を更新します。
 */
export default function AIChatSide({
  messages,
  userInput,
  setUserInput,
  isTyping = false,
  isLoading = false,
  chatLoading = false,
  reflectLoading = false,
  onChatSend,
  onReflectPlan,
  chatEndRef,
  initialGuideMessage = 'AIチャットへようこそ！何でも質問できます。',
  onClose,
  // 送信ボタンのラベルを引数で渡す
  chatButtonLabel = 'AIに相談',
  planButtonLabel = '採用計画を修正',
  planFeatureEnabled = false, // 採用計画修正機能の有効/無効を制御する新しいプロパティ
  featureKey = null, // 機能別テーマカラー用キー（'recruit_plan', 'media_selection', etc.）
}) {
  const theme = useTheme();
  // "chat" or "plan" を選択するトグル
  const [mode, setMode] = useState('chat');

  // メッセージがない場合はガイドメッセージを表示
  const mergedMessages =
    messages.length === 0
      ? [{ sender: 'bot', text: initialGuideMessage }]
      : messages;

  return (
    <Box
      sx={{
        width: 400,
        backgroundColor: bg(theme).card,
        boxShadow: '0 0 10px rgba(0,0,0,0.15)',
        display: 'flex',
        flexDirection: 'column',
        position: 'fixed',
        top: 0,
        right: 0,
        bottom: 0,
        zIndex: 9999,
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          py: 1.5,
          backgroundColor: featureKey && designTokens.featureThemes[featureKey]
            ? designTokens.featureThemes[featureKey].primary
            : '#249BE0',
          borderBottom: 'none',
        }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#fff' }}>
          AIアシスタント
        </Typography>
        <IconButton
          size="small"
          onClick={onClose}
          sx={{
            color: 'rgba(255,255,255,0.8)',
            '&:hover': {
              backgroundColor: 'rgba(255,255,255,0.1)',
              color: '#fff',
            }
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* メッセージ一覧 */}
      <Box
        sx={{
          flexGrow: 1,
          p: 2,
          overflowY: 'auto',
          ...chatBackground(featureKey, theme),
        }}
      >
        {mergedMessages.map((msg, i) => (
          <ChatBubble key={i} message={msg} featureKey={featureKey} />
        ))}
        {isTyping && <TypingBubble />}
        <div ref={chatEndRef} />
      </Box>

      {/* 入力エリア */}
      <Box
        sx={{
          p: 2,
          borderTop: '1px solid #e9ecef',
          backgroundColor: bg(theme).card,
        }}
      >
        <TextField
          variant="outlined"
          size="small"
          placeholder="相談または修正内容を入力..."
          value={userInput}
          onChange={(e) => setUserInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && e.shiftKey) {
              e.preventDefault();
              if (!planFeatureEnabled || mode === 'chat') {
                onChatSend && onChatSend(userInput);
              } else {
                onReflectPlan && onReflectPlan();
              }
            }
          }}
          sx={{
            width: '100%',
            mb: 1.5,
            ...chatInputField(featureKey, theme),
          }}
          multiline
          minRows={2}
          maxRows={6}
        />

        {/* Toggle + Sendボタン */}
        <Box display="flex" justifyContent="space-between" alignItems="center">
          {planFeatureEnabled ? (
            <ToggleButtonGroup
              value={mode}
              exclusive
              onChange={(_, newVal) => {
                if (newVal) setMode(newVal);
              }}
              sx={{
                borderRadius: '6px',
                overflow: 'hidden',
                backgroundColor: bg(theme).hover,
                border: `1px solid ${border(theme).subtle}`,
                '& .MuiToggleButton-root': {
                  border: 'none',
                  borderRadius: 0,
                  color: '#6b7280',
                  fontWeight: 600,
                  px: 2,
                  py: 0.75,
                  fontSize: '0.875rem',
                  textTransform: 'none',
                  '&.Mui-selected': {
                    color: '#fff',
                    backgroundColor: '#249BE0',
                  },
                  '&.Mui-selected:hover': {
                    backgroundColor: '#1d8bc7',
                  },
                  '&:hover': {
                    backgroundColor: border(theme).subtle,
                  }
                }
              }}
            >
              <ToggleButton value="chat">AIに相談</ToggleButton>
              <ToggleButton value="plan">計画を修正</ToggleButton>
            </ToggleButtonGroup>
          ) : (
            // 計画修正機能が無効の場合は "AIに相談" ラベルのみを表示
            <div></div>
          )}

          <Button
            onClick={() => {
              if (!planFeatureEnabled || mode === 'chat') {
                onChatSend && onChatSend(userInput);
              } else {
                onReflectPlan && onReflectPlan();
              }
            }}
            disabled={chatLoading || reflectLoading || isLoading}
            variant="contained"
            sx={{
              ml: 1,
              ...chatSendButton(featureKey),
              '&:active': {
                transform: 'scale(0.98)'
              }
            }}
            startIcon={
              (chatLoading || reflectLoading || isLoading) ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : <SendIcon />
            }
          >
            {!planFeatureEnabled || mode === 'chat'
              ? (chatLoading ? '送信中...' : chatButtonLabel)
              : (reflectLoading ? '処理中...' : planButtonLabel)}
          </Button>
        </Box>
      </Box>
    </Box>
  );
}